import { injectable } from "inversify";
import * as THREE from "three";
import { type IService, Logger } from "@designboticteam/core";

@injectable()
class MaterialFactoryService implements IService {
	public readonly serviceName = "🎨 MaterialFactoryService";
	private logger = new Logger(`🎬 WORKSPACE 3D | ${this.serviceName}`, {
		color: "#ffffff",
		backgroundColor: "#0066cc",
	});

	private colorCache = new Map<string, THREE.MeshStandardMaterial>();
	private groundMaterial: THREE.MeshStandardMaterial | null = null;
	private shadowMaterial: THREE.MeshBasicMaterial | null = null;
	private edgeMaterial: THREE.LineBasicMaterial | null = null;

	public initialize(): void {
		this.logger.log("initialized");
	}

	public getByColor(color: string): THREE.MeshStandardMaterial {
		const cached = this.colorCache.get(color);

		if (cached) {
			return cached;
		}

		const material = new THREE.MeshStandardMaterial({
			color: new THREE.Color(color),
			roughness: 0.95,
			metalness: 0.2,
			envMapIntensity: 1.5,
			side: THREE.DoubleSide,
			dithering: true,
			flatShading: true,
		});

		this.colorCache.set(color, material);

		return material;
	}

	public getDefault(): THREE.MeshStandardMaterial {
		return this.getByColor("#0f0f0f");
	}

	public getGround(): THREE.MeshStandardMaterial {
		if (this.groundMaterial === null) {
			this.groundMaterial = new THREE.MeshStandardMaterial({
				color: 0xffffff,
				transparent: true,
				opacity: 0.8,
				side: THREE.DoubleSide,
				roughness: 0.8,
				metalness: 0.1,
			});
		}

		return this.groundMaterial;
	}

	public getShadow(opacity = 0.3): THREE.MeshBasicMaterial {
		if (this.shadowMaterial === null) {
			this.shadowMaterial = new THREE.MeshBasicMaterial({
				color: 0x000000,
				transparent: true,
				side: THREE.DoubleSide,
				depthWrite: false,
				blending: THREE.MultiplyBlending,
			});
		}

		this.shadowMaterial.opacity = opacity;

		return this.shadowMaterial;
	}

	public getEdge(color = 0x000000, linewidth = 1): THREE.LineBasicMaterial {
		if (this.edgeMaterial === null) {
			this.edgeMaterial = new THREE.LineBasicMaterial({
				transparent: true,
				opacity: 0.8,
			});
		}
		this.edgeMaterial.color.setHex(color);
		this.edgeMaterial.linewidth = linewidth;
		return this.edgeMaterial;
	}

	public dispose(): void {
		for (const material of this.colorCache.values()) {
			material.dispose();
		}

		this.colorCache.clear();
		this.groundMaterial?.dispose();
		this.shadowMaterial?.dispose();
		this.edgeMaterial?.dispose();
		this.groundMaterial = null;
		this.shadowMaterial = null;
		this.edgeMaterial = null;
		this.logger.log("disposed");
	}
}

export { MaterialFactoryService };
